import {
  DocumentNode,
  GraphQLFieldConfig,
  GraphQLInputObjectType
} from 'graphql'
import { FieldsConfig } from '.'
import { Context } from '../../shared'
import { GenericMutationResponseType } from '../../types/generic-mutation-response'
import { buildMutation } from './build-mutation'

interface BuildMutationModuleExportsProps<
  TArgsInputType,
  TFieldConfigKey extends string
> {
  readonly description: string
  readonly inputType: GraphQLInputObjectType
  readonly resolve: CustomResolver<TArgsInputType>
  readonly mutationName: string
  readonly fieldsConfig: FieldsConfig<TFieldConfigKey>
}

interface MutationArgs<TArgsInputType> {
  readonly input: TArgsInputType
}

interface BuildMutationModuleExportsResponse<TArgsInputType> {
  readonly mutationName: string
  readonly mutationConfig: GraphQLFieldConfig<
    undefined,
    Context,
    MutationArgs<TArgsInputType>
  >
  readonly mutation: DocumentNode
  readonly mutationString: string
}

/**
 * @description Use this to build mutations
 * @example
 * export const = {
 *  mutationName,
 *  mutationConfig,
 *  mutation,
 *  mutationString
 * } = buildMutationModuleExports({
 *  mutationName: 'deleteAppLibrariesWithFilters',
 *  description: 'Mutation to delete application libraries found by filters',
 *  inputType,
 *  fieldsConfig,
 *  resolve
 * })
 */
export function buildMutationModuleExports<
  TArgsInputType,
  TFieldConfigKey extends string
>({
  mutationName,
  description,
  inputType,
  fieldsConfig,
  resolve
}: BuildMutationModuleExportsProps<
  TArgsInputType,
  TFieldConfigKey
>): BuildMutationModuleExportsResponse<TArgsInputType> {
  const { mutationString, mutation } = buildMutation<TFieldConfigKey>({
    mutationName,
    fieldsConfig
  })

  return {
    mutationName,
    mutationConfig: buildConfig({
      mutationName,
      description,
      inputType,
      resolve
    }),
    mutation,
    mutationString
  }
}

type CustomResolver<TArgsInputType> = (
  connector: Context['connector'],
  input: TArgsInputType
) => Promise<void>

interface MutationConfig<TArgsInputType> {
  readonly mutationName: string
  readonly description: string
  readonly inputType: GraphQLInputObjectType
  readonly resolve: CustomResolver<TArgsInputType>
}

function buildConfig<TArgsInputType>({
  mutationName,
  description,
  inputType,
  resolve
}: MutationConfig<TArgsInputType>): GraphQLFieldConfig<
  undefined,
  Context,
  MutationArgs<TArgsInputType>
> {
  return {
    description,
    type: GenericMutationResponseType,
    args: {
      input: {
        type: inputType
      }
    },
    async resolve(_source, args, { connector }) {
      if (!args.input) {
        throw new Error('mutation input parameter is required')
      }

      // Call custom resolve function
      // Gracefully handle errors here and report a nice error message
      // Only pass the values that the custom function needs
      try {
        await resolve(connector, args.input)
      } catch (error) {
        const message = `Mutation failed. mutationName=${mutationName} args=${JSON.stringify(
          args
        )}`
        console.error(message, error)
        throw new Error(message)
      }

      return {
        _placeholder: true
      }
    }
  }
}
